import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { ReactComponent as ThumbUp } from "../../../assets/svg/thumb-up.svg";
import { ReactComponent as ThumbUpActive } from "../../../assets/svg/thumb-up-active.svg";
import { ReactComponent as ReplyIcon } from "../../../assets/svg/message-bubble.svg";
import { ReactComponent as DotsHorizontalIcon } from "../../../assets/svg/dots-horizontal.svg";
import Avatar from "../../../components/common/AvtarImage";
import appRoutes from "../../../routes/app.routes";
import {
  convertFormattedEditTime,
  convertUTCToLocal,
  getUserDisplayJobTitle,
  openUrlInNewTab,
} from "../../../utils/helper";

interface FeedCommentItemProps {
  item: any;
  postOwnerId: number | string;
  currentUserId: number | string;
  showReplyAction?: boolean;
  onLike: (item: any) => void;
  onReply?: (item: any) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  onReport?: (item: any) => void;
  body?: React.ReactNode;
  isReply?: boolean;
  isReplyActive?: boolean;
  editComposer?: React.ReactNode;
  replyComposer?: React.ReactNode;
  isEditing?: boolean;
}

const FeedCommentItem = ({
  item,
  postOwnerId,
  currentUserId,
  showReplyAction = true,
  onLike,
  onReply,
  onEdit,
  onDelete,
  onReport,
  body,
  isReply = false,
  isReplyActive = false,
  editComposer,
  replyComposer,
  isEditing = false,
}: FeedCommentItemProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isAuthor = String(postOwnerId) === String(item.user_id);
  const isYou = String(currentUserId) === String(item.user_id);
  const isLoggedIn = Boolean(currentUserId);
  const jobTitle = getUserDisplayJobTitle(item.user);
  const likeCount = _.get(item, "comment_likes_count", 0);
  const isLiked = _.get(item, "liked", 0) === 1;
  const fullName =
    `${_.get(item, "user.first_name", "")} ${_.get(item, "user.last_name", "")}`.trim();

  const isEdited = item?.is_edited === true || item?.is_edited === 1;
  const formattedDate = convertUTCToLocal(item.created_at);
  const subtitleParts = [
    jobTitle,
    formattedDate,
    isEdited ? "(edited)" : null,
  ].filter(Boolean);

  const openProfile = () => {
    const slug = item?.user?.slug;
    if (slug) {
      openUrlInNewTab(appRoutes.candidatePublicProfile.generateFullPath(slug));
    }
  };

  const runMenuAction = (action?: (item: any) => void) => {
    if (!action) return;
    action(item);
    setMenuOpen(false);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [menuOpen]);

  const hasMenu =
    (isYou && (onEdit || onDelete)) || (!isYou && onReport);

  return (
    <article
      className={`feed-comment ${isReply ? "is-reply" : "is-root"}${isEditing ? " is-editing" : ""}`}
    >
      <div className="feed-comment__avatar">
        <Avatar src={item?.user?.profile_image} alt={fullName} />
      </div>

      <div className="feed-comment__content">
        <div className="feed-comment__header">
          <div className="feed-comment__meta">
            <div className="feed-comment__name-row">
              <button type="button" className="feed-comment__name" onClick={openProfile}>
                {fullName}
              </button>
              {isAuthor && (
                <span className="comment-badge author">Author</span>
              )}
              {isLoggedIn && isYou && (
                <span className="comment-badge you">You</span>
              )}
            </div>
            <div className="feed-comment__subtitle">
              {subtitleParts.map((part, index) => (
                <React.Fragment key={`${part}-${index}`}>
                  {index > 0 && " • "}
                  {part === "(edited)" ? (
                    <span
                      className="edited-inline"
                      title={
                        item.formatted_edit_time
                          ? `Edited on ${convertFormattedEditTime(item.formatted_edit_time)}`
                          : undefined
                      }
                    >
                      {part}
                    </span>
                  ) : (
                    part
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {hasMenu && (
            <div className="feed-comment__menu" ref={menuRef}>
              <button
                type="button"
                className="comment-menu-btn"
                aria-label="Comment options"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((prev) => !prev)}
              >
                <DotsHorizontalIcon className="dot-icon" />
              </button>
              {menuOpen && (
                <ul className="comment-menu-dropdown">
                  {isYou && item.comment_type !== "image" && onEdit && (
                    <li>
                      <button type="button" onClick={() => runMenuAction(onEdit)}>
                        Edit
                      </button>
                    </li>
                  )}
                  {isYou && onDelete && (
                    <li>
                      <button type="button" onClick={() => runMenuAction(onDelete)}>
                        Delete
                      </button>
                    </li>
                  )}
                  {!isYou && onReport && (
                    <li>
                      <button type="button" onClick={() => runMenuAction(onReport)}>
                        Report this comment
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>

        {!isEditing && body != null && (
          <div className="feed-comment__body">{body}</div>
        )}

        {isEditing && editComposer && (
          <div className="feed-comment__edit">{editComposer}</div>
        )}

        {!isEditing && (
          <footer className="feed-comment__footer">
            <button
              type="button"
              className={`comment-action like-action ${isLiked ? "active" : ""}`}
              onClick={() => onLike(item)}
            >
              {isLiked ? <ThumbUpActive aria-hidden /> : <ThumbUp aria-hidden />}
              <span>
                {likeCount} {likeCount === 1 ? "Like" : "Likes"}
              </span>
            </button>
            {showReplyAction && onReply && (
              <button
                type="button"
                className={`comment-action reply-action ${isReplyActive ? "active" : ""}`}
                onClick={() => onReply(item)}
              >
                <ReplyIcon aria-hidden />
                <span>Reply</span>
              </button>
            )}
          </footer>
        )}

        {!isEditing && replyComposer && (
          <div className="feed-comment__reply">{replyComposer}</div>
        )}
      </div>
    </article>
  );
};

export default FeedCommentItem;
